import { useState, type Dispatch, type SetStateAction } from 'react'

// State that snaps back to a fresh initial value whenever `key` changes.
//
// The reset happens during render (React's documented "adjusting state when a
// prop changes" pattern) rather than in an effect, so the component never
// paints one frame with the previous subject's state — a dialog reopened on a
// different artist can't flash the old artist's draft. It also avoids the
// cascading re-render an effect-based `setState(propDerivedValue)` causes.
//
// `init` is called on mount and on every key change; keep it cheap and pure.
export function useResetOnChange<T>(
  key: unknown,
  init: () => T,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState(init)
  const [prevKey, setPrevKey] = useState(key)

  if (prevKey !== key) {
    setPrevKey(key)
    setState(init())
  }

  return [state, setState]
}
